import { useCallback, useEffect, useState } from 'react';

export type CartLine = {
  key: string;
  productId: string;
  name: string;
  quantity: number;
  optionIds: string[];
  optionLabels: string[];
  unitPriceCents: number;
  notes: string | null;
};

function read(storageKey: string): CartLine[] {
  try {
    const raw = sessionStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as CartLine[]) : [];
  } catch {
    return [];
  }
}

/**
 * Carrinho local (só exibição). Os preços aqui servem para o cliente conferir;
 * o valor cobrado é sempre recalculado pelo servidor.
 */
export function useCart(storageKey: string) {
  const [lines, setLines] = useState<CartLine[]>(() => read(storageKey));
  useEffect(() => setLines(read(storageKey)), [storageKey]);
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(lines));
    } catch {
      /* armazenamento indisponível (aba anônima): o carrinho fica só em memória */
    }
  }, [storageKey, lines]);

  const add = useCallback((line: Omit<CartLine, 'key'>) => {
    setLines((ls) => {
      // Mesmo produto com as mesmas opções e observação: soma a quantidade.
      const same = ls.find((l) => l.productId === line.productId && l.notes === line.notes && [...l.optionIds].sort().join() === [...line.optionIds].sort().join());
      if (same) return ls.map((l) => (l === same ? { ...l, quantity: l.quantity + line.quantity } : l));
      return [...ls, { ...line, key: crypto.randomUUID() }];
    });
  }, []);
  const setQty = useCallback((key: string, quantity: number) => {
    setLines((ls) => (quantity <= 0 ? ls.filter((l) => l.key !== key) : ls.map((l) => (l.key === key ? { ...l, quantity } : l))));
  }, []);
  const clear = useCallback(() => setLines([]), []);

  const totalCents = lines.reduce((s, l) => s + l.unitPriceCents * l.quantity, 0);
  const count = lines.reduce((s, l) => s + l.quantity, 0);
  return { lines, add, setQty, clear, totalCents, count };
}
export type Cart = ReturnType<typeof useCart>;
